// Type definitions for Meta Pixel
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

// Type definitions for Cloudflare D1
export interface Product {
  id: number;
  name: string;
  purchase_value_usd: number;
  estimated_weight_kg: number;
  shipping_cost_per_kg_usd: number;
  purchase_link: string;
  created_at?: string;
}

export interface ProductsResponse {
  success: boolean;
  data: Product[];
  error?: string;
}

export const STANDARD_EVENTS = [
  { value: 'AddPaymentInfo', label: 'Add Payment Info', desc: 'Adding payment information during checkout' },
  { value: 'AddToCart', label: 'Add to Cart', desc: 'Adding an item to a shopping cart' },
  { value: 'AddToWishlist', label: 'Add to Wishlist', desc: 'Adding items to a wishlist' },
  { value: 'CompleteRegistration', label: 'Complete Registration', desc: 'Submitting info in exchange for service' },
  { value: 'Contact', label: 'Contact', desc: 'Phone, SMS, email, chat, or other contact' },
  { value: 'CustomizeProduct', label: 'Customize Product', desc: 'Customizing products via a tool' },
  { value: 'Donate', label: 'Donate', desc: 'Donating funds to your organization' },
  { value: 'FindLocation', label: 'Find Location', desc: 'Finding a physical location via web' },
  { value: 'InitiateCheckout', label: 'Initiate Checkout', desc: 'The start of a checkout process' },
  { value: 'Lead', label: 'Lead', desc: 'Submission of info with understanding of contact' },
  { value: 'Purchase', label: 'Purchase', desc: 'Completion of a purchase (Thank You page)' },
  { value: 'Schedule', label: 'Schedule', desc: 'Booking an appointment' },
  { value: 'Search', label: 'Search', desc: 'Search performed on your website' },
  { value: 'StartTrial', label: 'Start Trial', desc: 'Start of a free trial' },
  { value: 'SubmitApplication', label: 'Submit Application', desc: 'Application for product, service, or program' },
  { value: 'Subscribe', label: 'Subscribe', desc: 'Start of a paid subscription' },
  { value: 'ViewContent', label: 'View Content', desc: 'Visit to a specific web page' },
];

export const EXAMPLE_CONTEXT_CODE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <link rel="icon" href="%PUBLIC_URL%/favicon.ico" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="theme-color" content="#000000" />
  <meta name="description" content="Web site created using create-react-app" />
  <title>React App</title>
  <!-- 🟢 PASTE META PIXEL HERE 🟢 -->
</head>
<body>
  <noscript>You need to enable JavaScript to run this app.</noscript>
  <div id="root"></div>
</body>
</html>`;

interface TrackingParams {
  pixelId: string;
  eventType: string;
  userData: {
    fn: string;
    ln: string;
    ph: string;
    ge: string;
    cn: string;
  };
  eventData: {
    value: string;
    currency: string;
    contentName: string;
  };
}

// Helper to inject the Pixel script if it doesn't exist
export const installPixel = () => {
  if (window.fbq) return; // Already installed

  // Meta Pixel Base Code
  (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
};

// Function to actually fire the event
export const fireEvent = ({ pixelId, eventType, userData, eventData }: TrackingParams) => {
  if (!pixelId) throw new Error('Pixel ID is required');

  // Ensure base code is present
  installPixel();

  // clean user data (remove empty strings)
  const cleanUserData = Object.fromEntries(
    Object.entries(userData).filter(([_, v]) => v !== '')
  );

  // clean event data
  const cleanEventData: any = {};
  if (eventData.contentName) {
    cleanEventData.content_name = eventData.contentName;
    cleanEventData.content_type = 'product';
  }
  if (eventData.value) cleanEventData.value = parseFloat(eventData.value);
  if (eventData.currency) cleanEventData.currency = eventData.currency;

  // Initialize with Pixel ID and User Data (Advanced Matching)
  // We re-init to ensure the user data is attached to this specific event sequence
  window.fbq('init', pixelId, cleanUserData);

  // Track the Standard Event
  window.fbq('track', eventType, cleanEventData);
  
  return {
    timestamp: new Date().toLocaleTimeString(),
    type: eventType,
    payload: { ...cleanEventData, user: cleanUserData }
  };
};